// Source-level selector resolver for `var(...)` references before `{`.
// This pass keeps declaration bodies untouched and rewrites selector headers only.
import { resolveSelectorReferences } from './selector'
import type { ClassTransformOptions } from './types'

// Resolve selector-side variable references across full source text.
export function resolveSelectorReferencesInSource(source: string, options: ClassTransformOptions): string {
  let output = ''
  let buffer = ''
  let quote: "'" | '"' | null = null
  let escape = false

  // Scan source once and resolve header buffers on each block opener.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]

    // Keep quoted content opaque while preserving escape sequences.
    if (quote) {
      buffer += char
      if (escape) {
        escape = false
      } else if (char === '\\') {
        escape = true
      } else if (char === quote) {
        quote = null
      } else {
        // Continue quoted-source scanning.
      }
      continue
    } else {
      // Continue unquoted source scanning.
    }

    // Enter quote mode for string content.
    if (char === "'" || char === '"') {
      quote = char
      buffer += char
      continue
    } else {
      // Continue structural-token scanning.
    }

    // Resolve buffered selector header right before `{`.
    if (char === '{') {
      output += resolveSelectorHeaderBuffer(buffer, options)
      output += '{'
      buffer = ''
      continue
    } else if (char === ';' || char === '}') {
      // Flush non-selector buffer unchanged at statement/block boundaries.
      output += buffer
      output += char
      buffer = ''
      continue
    } else {
      // Continue buffering structural context text.
    }

    buffer += char
  }

  output += buffer
  // Return source with selector-header references resolved.
  return output
}

// Resolve one selector-header candidate buffer when it is selector-like.
function resolveSelectorHeaderBuffer(buffer: string, options: ClassTransformOptions): string {
  const trimmed = buffer.trim()
  // Keep empty and at-rule headers unchanged.
  if (!trimmed || trimmed.startsWith('@') || !buffer.includes('var(')) {
    return buffer
  } else {
    // Continue selector header resolution.
  }

  // Return selector buffer with resolved var() references.
  return resolveSelectorReferences(buffer, options)
}
